import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronLeft, CircleCheck, ExternalLink, FileText, ImageOff, TriangleAlert } from 'lucide-react';
import {
  isDarkColor,
  isQuizSlideType,
  quizHint,
  safeMediaUrl,
  slideBackground,
  youtubeEmbedUrl,
  youtubeId,
  type LmsPreviewSlide,
} from '../utils/lmsSlidePreview';

// A phone-sized frame that walks through a module's slides the way the learner's app
// shows them (apps/mobile/src/screens/lms/LmsContentScreen.tsx): the module's title and
// progress at the top, one slide, Previous / Next at the bottom. It is a viewer only:
// nothing here answers a quiz, starts a timer or saves anything, and quiz slides show
// their answer key instead of asking for an answer.

const OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
// As in the app: one segment per slide, a single bar once there are too many to tell apart.
const MAX_PROGRESS_SEGMENTS = 40;
// Below this share of the slide's width, a height-fitted image is too narrow to read
// (a tall infographic), so it fills the width and scrolls instead. The app's rule.
const MIN_FIT_WIDTH_RATIO = 0.75;
// The app shows an image slide full screen on a phone, about this many points across
// and down. The rule above is judged on that shape, not on this frame, which is shorter
// on a laptop: a portrait photo the learner sees whole is shown whole here too.
const PHONE_IMAGE_AREA = { width: 390, height: 760 };
// A Done that lands this soon after the slide changed is the second click of a double
// click on Next (Next turns into Done in the same place): it does not leave the preview.
const DONE_GUARD_MS = 400;

interface LmsSlidePreviewFrameProps {
  moduleTitle: string;
  /** Active slides in the order the learner gets them. */
  slides: LmsPreviewSlide[];
  /** "Done" on the last slide. */
  onDone: () => void;
  /** Where the empty state's "Edit module" link goes. */
  editHref: string;
}

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
};

export default function LmsSlidePreviewFrame({ moduleTitle, slides, onDone, editHref }: LmsSlidePreviewFrameProps) {
  const [index, setIndex] = useState(0);
  const count = slides.length;
  const current = count > 0 ? slides[Math.min(index, count - 1)] : null;
  const position = Math.min(index, Math.max(count - 1, 0));
  const isLast = position === count - 1;
  const nextButton = useRef<HTMLButtonElement>(null);
  const changedAt = useRef(0);

  const goTo = (i: number) => {
    changedAt.current = Date.now();
    setIndex(Math.max(0, Math.min(count - 1, i)));
  };

  // Left / Right move between slides, Home / End to the first and last, unless the
  // admin is typing somewhere.
  useEffect(() => {
    if (count === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || isTyping(e.target)) return;
      e.preventDefault();
      changedAt.current = Date.now();
      setIndex((i) => {
        if (e.key === 'Home') return 0;
        if (e.key === 'End') return count - 1;
        return e.key === 'ArrowLeft' ? Math.max(0, i - 1) : Math.min(count - 1, i + 1);
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [count]);

  const quizIds = slides.filter((s) => isQuizSlideType(s.slide_type)).map((s) => s.id);

  return (
    <div className="mx-auto w-full max-w-[390px]">
      <section
        aria-label="Phone preview"
        className="flex flex-col overflow-hidden rounded-[2.25rem] border-[6px] border-gray-900 bg-white shadow-2xl sm:border-[10px]"
        style={{ height: 'min(780px, calc(100vh - 230px))', minHeight: 560 }}
      >
        {/* Top bar: the module, as the app's header shows it, then the progress. */}
        <div className="shrink-0 border-b border-gray-100 bg-white px-4 pb-3 pt-4">
          <p className="truncate text-center text-[15px] font-bold text-gray-900" title={moduleTitle}>
            {moduleTitle}
          </p>
          {current && (
            <>
              <div
                role="progressbar"
                aria-label="Slide progress"
                aria-valuemin={1}
                aria-valuemax={count}
                aria-valuenow={position + 1}
                className="mt-1.5"
              >
                {count <= MAX_PROGRESS_SEGMENTS ? (
                  <div className="flex gap-1">
                    {/* Each segment jumps to its slide: a 30-slide module need not be stepped through. */}
                    {slides.map((s, i) => (
                      <button
                        key={`${s.id}-${i}`}
                        type="button"
                        onClick={() => goTo(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        aria-current={i === position ? 'step' : undefined}
                        className="group flex-1 py-1.5"
                      >
                        <span
                          className={`block h-1 rounded-full group-hover:opacity-80 ${
                            i < position ? 'bg-primary-600/40' : i === position ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="h-1 overflow-hidden rounded-full bg-gray-300">
                    <div className="h-full bg-primary-600" style={{ width: `${((position + 1) / count) * 100}%` }} />
                  </div>
                )}
              </div>
              <div className="mt-2.5 flex justify-end">
                <span aria-live="polite" className="rounded-full bg-gray-700 px-3 py-1 text-xs font-semibold tabular-nums text-white">
                  Slide {position + 1} of {count}
                </span>
              </div>
            </>
          )}
        </div>

        {current ? (
          <>
            <SlideArea key={`${current.id}-${position}`} slide={current} position={position} count={count} quizIds={quizIds} />

            <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 bg-white px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  goTo(position - 1);
                  // Back on the first slide this button is disabled, and focus would drop
                  // to the page: hand it to Next instead.
                  if (position - 1 <= 0) nextButton.current?.focus();
                }}
                disabled={position === 0}
                aria-label="Previous slide"
                title="Previous"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                ref={nextButton}
                type="button"
                onClick={() => {
                  if (!isLast) goTo(position + 1);
                  else if (Date.now() - changedAt.current >= DONE_GUARD_MS) onDone();
                }}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-primary-600 text-[15px] font-semibold text-white hover:bg-primary-700"
              >
                {isLast ? 'Done' : 'Next'}
                {isLast ? <Check className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center p-10 text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50">
              <FileText className="h-10 w-10 text-primary-600" />
            </div>
            <p className="mb-2 text-lg font-semibold text-gray-800">This module has no slides yet</p>
            <p className="mb-6 text-sm leading-5 text-gray-500">Add slides in the editor and save them to preview them here.</p>
            <Link
              to={editHref}
              className="rounded-xl bg-primary-50 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-100"
            >
              Edit module
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

interface SlideAreaProps {
  slide: LmsPreviewSlide;
  position: number;
  count: number;
  quizIds: string[];
}

/** The slide itself: the part between the top bar and the buttons. Keyed per slide, so it opens scrolled to the top. */
function SlideArea({ slide, position, count, quizIds }: SlideAreaProps) {
  const imageSrc = slide.slide_type === 'image' ? safeMediaUrl(slide.media_url) : null;
  if (imageSrc) {
    return (
      <div data-testid="preview-slide" className="relative min-h-0 flex-1 overflow-hidden bg-gray-900">
        <FullImage src={imageSrc} alt={slide.title || `Slide ${position + 1} of ${count}`} />
      </div>
    );
  }
  const background = slide.slide_type === 'text' ? slideBackground(slide.background_color) : null;
  return (
    <div
      data-testid="preview-slide"
      className="min-h-0 flex-1 overflow-y-auto bg-white"
      style={background ? { backgroundColor: background } : undefined}
    >
      <SlideContent slide={slide} background={background} quizIds={quizIds} />
    </div>
  );
}

function SlideContent({ slide, background, quizIds }: { slide: LmsPreviewSlide; background: string | null; quizIds: string[] }) {
  switch (slide.slide_type) {
    case 'text': {
      const onDark = !!background && isDarkColor(background);
      return (
        <div className="px-6 py-6">
          {slide.title && (
            <h2 className={`mb-4 break-words text-[26px] font-extrabold leading-8 tracking-tight ${onDark ? 'text-white' : 'text-gray-900'}`}>
              {slide.title}
            </h2>
          )}
          {slide.body_text && (
            <p className={`whitespace-pre-wrap break-words text-[17px] leading-[26px] ${onDark ? 'text-white/90' : 'text-gray-700'}`}>
              {slide.body_text}
            </p>
          )}
          {!slide.title && !slide.body_text && (
            <h2 className={`text-[26px] font-extrabold leading-8 tracking-tight ${onDark ? 'text-white' : 'text-gray-900'}`}>
              No content available
            </h2>
          )}
        </div>
      );
    }

    case 'image':
      // An image with a file fills the slide (FullImage); this is the no-file state.
      return (
        <div className="flex h-full flex-col px-5 pb-2 pt-4">
          {slide.title && <h2 className="mb-3 break-words text-[22px] font-extrabold tracking-tight text-gray-900">{slide.title}</h2>}
          <div className="flex flex-1 items-center justify-center text-base text-gray-500">Image not available</div>
        </div>
      );

    case 'pdf': {
      const src = safeMediaUrl(slide.media_url);
      return (
        <div className="flex h-full flex-col px-5 pb-3 pt-4">
          {slide.title && <h2 className="mb-3 break-words text-[22px] font-extrabold tracking-tight text-gray-900">{slide.title}</h2>}
          {src ? (
            <>
              <iframe
                title={slide.title ? `PDF: ${slide.title}` : 'PDF document'}
                src={src}
                className="min-h-[320px] w-full flex-1 rounded-xl border border-gray-200 bg-gray-100"
              />
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-600 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Open in a new tab
              </a>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-base text-gray-500">PDF not available</div>
          )}
          {slide.body_text && (
            <p className="mt-3 max-h-[100px] shrink-0 overflow-y-auto whitespace-pre-wrap break-words text-[15px] leading-[22px] text-gray-600">
              {slide.body_text}
            </p>
          )}
        </div>
      );
    }

    case 'video': {
      const link = slide.video_embed_url.trim();
      const videoId = link ? youtubeId(link) : null;
      return (
        <div className="flex min-h-full flex-col justify-center p-5">
          {slide.title && <h2 className="mb-5 break-words text-center text-2xl font-bold text-gray-800">{slide.title}</h2>}
          {videoId ? (
            <div className="aspect-video w-full overflow-hidden rounded-xl bg-black shadow-md">
              <iframe
                title={slide.title ? `Video: ${slide.title}` : 'YouTube video'}
                src={youtubeEmbedUrl(videoId)}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                // YouTube refuses an embed that arrives with no referrer (its "Error 153"),
                // and the site's own policy sends none; this frame sends the site's origin.
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ) : (
            <div className="text-center">
              <p className="text-base text-gray-500">Video URL not available</p>
              {/* What the learner sees above; what the admin needs to know below. */}
              {link && (
                <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-left text-sm font-medium text-amber-800">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  Learners can&apos;t play this link: only YouTube links play in the app
                </div>
              )}
            </div>
          )}
          {slide.body_text && (
            <p className="mt-5 whitespace-pre-wrap break-words text-center text-base text-gray-700">{slide.body_text}</p>
          )}
        </div>
      );
    }

    case 'quiz_single':
    case 'quiz_multiple':
    case 'quiz_truefalse': {
      const options = slide.quiz_options;
      const hasCorrect = options.some((o) => o.is_correct);
      return (
        <div className="px-5 pb-6 pt-5">
          <span className="inline-block rounded-full border border-primary-100 bg-primary-50 px-3.5 py-1.5 text-xs font-bold tracking-wider text-primary-600">
            KNOWLEDGE CHECK · {quizIds.indexOf(slide.id) + 1} / {quizIds.length}
          </span>
          <h2 className="mt-5 whitespace-pre-wrap break-words text-[26px] font-extrabold leading-8 tracking-tight text-gray-900">
            {slide.quiz_question}
          </h2>
          <p className="mb-5 mt-3 text-[15px] text-gray-500">{quizHint(slide.slide_type)}</p>
          {!hasCorrect && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-800">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              No correct answer is set
            </div>
          )}
          <ul aria-label="Answer options" className="space-y-3">
            {options.map((option, i) => (
              <li
                key={`${option.id}-${i}`}
                className={`flex items-center gap-3.5 rounded-2xl border-[1.5px] px-3.5 py-4 ${
                  option.is_correct ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 bg-white'
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ${
                    option.is_correct ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {OPTION_LETTERS[i] ?? i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-base leading-[22px] text-gray-800">{option.text}</span>
                  {option.is_correct && (
                    <span className="mt-1 block text-xs font-semibold text-emerald-700">Correct answer</span>
                  )}
                </span>
                {option.is_correct && <CircleCheck className="h-[22px] w-[22px] shrink-0 text-emerald-600" aria-hidden="true" />}
              </li>
            ))}
          </ul>
          {slide.quiz_explanation && (
            <div className="mt-6 rounded-xl bg-amber-100 p-4">
              <p className="mb-2 text-sm font-semibold text-amber-800">Explanation</p>
              <p className="whitespace-pre-wrap break-words text-sm leading-5 text-amber-900">{slide.quiz_explanation}</p>
            </div>
          )}
        </div>
      );
    }

    default:
      return (
        <div className="flex h-full items-center justify-center p-6 text-center text-base text-gray-500">
          Content not available for type: {slide.slide_type || 'unknown'}
        </div>
      );
  }
}

/**
 * An image slide as the app shows it: the whole image fitted to the slide, on a blurred,
 * dimmed copy of itself. A very tall image fills the width and scrolls instead.
 */
function FullImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  const [tall, setTall] = useState(false);

  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (!w || !h || tall) return;
    const ratio = w / h;
    const { width, height } = PHONE_IMAGE_AREA;
    const fitsHeight = width / ratio <= height;
    if (!fitsHeight && height * ratio < width * MIN_FIT_WIDTH_RATIO) setTall(true);
  };

  return (
    <div className="absolute inset-0">
      {!failed && (
        <img src={src} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl" />
      )}
      <div className="absolute inset-0 bg-black/40" />
      {failed ? (
        <div className="relative flex h-full flex-col items-center justify-center gap-2 text-white/70">
          <ImageOff className="h-10 w-10" aria-hidden="true" />
          <p className="text-[15px]">Image could not be loaded</p>
        </div>
      ) : tall ? (
        <div className="relative h-full overflow-y-auto">
          <img src={src} alt={alt} className="block h-auto w-full" onError={() => setFailed(true)} />
        </div>
      ) : (
        <img src={src} alt={alt} onLoad={onLoad} onError={() => setFailed(true)} className="relative h-full w-full object-contain" />
      )}
    </div>
  );
}
